import React, { useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import serviziData from '../../data/servizi-data';
import '../../styles/servizi.css';

const ServiceIllustration = ({ servizio }) => {
	if (servizio.heroImage) {
		return (
			<div className="service-illustration service-illustration--photo">
				<img
					src={servizio.heroImage}
					alt={servizio.title}
					className="service-hero-image"
					loading="eager"
				/>
				<div
					className="illustration-glow"
					style={{ background: servizio.gradient, opacity: 0.2 }}
				></div>
			</div>
		);
	}

	const icons = servizio.bannerIcons || [];
	return (
		<div className="service-illustration">
			<div className="illustration-grid">
				{icons.map((icon, i) => (
					<div
						key={i}
						className={`illustration-icon-bubble bubble-${i}`}
						style={{ '--accent': servizio.accentColor }}
					>
						<iconify-icon icon={icon}></iconify-icon>
					</div>
				))}
			</div>
			<div
				className="illustration-glow"
				style={{ background: servizio.gradient, opacity: 0.15 }}
			></div>
		</div>
	);
};

const ServicePage = () => {
	const { slug } = useParams();
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const servizioIndex = serviziData.findIndex((s) => s.slug === slug);
	const servizio = serviziData[servizioIndex];

	const goToContact = useCallback((e) => {
		e.preventDefault();
		navigate('/#contact');
		setTimeout(() => {
			const el = document.getElementById('contact');
			if (el) el.scrollIntoView({ behavior: 'smooth' });
		}, 100);
	}, [navigate]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [slug]);

	if (!servizio) {
		return (
			<section className="service-page-not-found">
				<div className="container-modern">
					<h2>{t('services.notFound')}</h2>
					<Link to="/" className="btn-modern btn-primary">
						{t('services.backHome')}
					</Link>
				</div>
			</section>
		);
	}

	const servicesTranslations = t('servicesData', { returnObjects: true });
	const st = servicesTranslations[servizioIndex] || {};

	const title = st.title || servizio.title;
	const subtitle = st.subtitle || servizio.subtitle;
	const shortDescription = st.shortDescription || servizio.shortDescription;
	const longDescription = st.longDescription || servizio.longDescription;
	const metaDescription = st.metaDescription || servizio.metaDescription || shortDescription;
	const features = st.features || servizio.features;
	const useCases = st.useCases || servizio.useCases;

	const pageUrl = `https://www.ingegneriabernasconi.it/servizi/${servizio.slug}`;
	const jsonLd = {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'Service',
				'@id': `${pageUrl}#service`,
				name: title,
				description: metaDescription,
				provider: {
					'@id': 'https://www.ingegneriabernasconi.it/#organization',
				},
				areaServed: { '@type': 'Country', name: 'Italia' },
				url: pageUrl,
			},
			{
				'@type': 'WebPage',
				'@id': `${pageUrl}#webpage`,
				url: pageUrl,
				name: `${title} | Ing. Eleonora Bernasconi`,
				description: metaDescription,
				isPartOf: {
					'@id': 'https://www.ingegneriabernasconi.it/#website',
				},
				about: { '@id': `${pageUrl}#service` },
				inLanguage: i18n.language,
			},
			{
				'@type': 'BreadcrumbList',
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						name: 'Home',
						item: 'https://www.ingegneriabernasconi.it/',
					},
					{
						'@type': 'ListItem',
						position: 2,
						name: t('nav.services'),
						item: 'https://www.ingegneriabernasconi.it/',
					},
					{
						'@type': 'ListItem',
						position: 3,
						name: title,
						item: pageUrl,
					},
				],
			},
			{
				'@type': 'FAQPage',
				mainEntity: features.map((f) => ({
					'@type': 'Question',
					name: `${t('services.faqPrefix')} ${f.title}?`,
					acceptedAnswer: {
						'@type': 'Answer',
						text: f.description,
					},
				})),
			},
		],
	};

	return (
		<React.Fragment>
			<Helmet>
				<html lang={i18n.language} />
				<title>{title} | Ing. Eleonora Bernasconi - Studio di Ingegneria</title>
				<meta name="description" content={metaDescription} />
				<meta name="keywords" content={servizio.metaKeywords || ''} />
				<link rel="canonical" href={pageUrl} />

				<meta property="og:title" content={`${title} | Ing. Eleonora Bernasconi`} />
				<meta property="og:description" content={metaDescription} />
				<meta property="og:url" content={pageUrl} />
				<meta property="og:type" content="website" />
				<meta property="og:locale" content={i18n.language === 'it' ? 'it_IT' : i18n.language} />
				<meta property="og:site_name" content="Ing. Eleonora Bernasconi - Studio di Ingegneria" />
				<meta property="og:image" content="https://www.ingegneriabernasconi.it/images/ingegneriabernasconi.jpg" />

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={`${title} | Ing. Eleonora Bernasconi`} />
				<meta name="twitter:description" content={metaDescription} />
				<meta name="twitter:image" content="https://www.ingegneriabernasconi.it/images/ingegneriabernasconi.jpg" />

				<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />

				<script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
			</Helmet>

			{/* Hero Banner */}
			<section
				className="service-hero"
				style={{ background: servizio.gradient }}
			>
				<div className="service-hero-pattern"></div>
				<div className="container-modern service-hero-content">
					<Link to="/#about" className="service-back-link">
						<iconify-icon icon="carbon:arrow-left"></iconify-icon>
						{t('services.allServices')}
					</Link>
					<div className="service-hero-layout">
						<div className="service-hero-text">
							<div className="service-hero-icon-badge">
								<iconify-icon icon={servizio.icon}></iconify-icon>
							</div>
							<h1 className="service-hero-title">{title}</h1>
							<p className="service-hero-subtitle">{subtitle}</p>
							<a href="/#contact" onClick={goToContact} className="btn-modern btn-hero-cta">
								<iconify-icon icon="carbon:email"></iconify-icon>
								{t('services.requestInfo')}
							</a>
						</div>
						<div className="service-hero-visual">
							<ServiceIllustration servizio={servizio} />
						</div>
					</div>
				</div>
			</section>

			{/* Content Banner */}
			<section className="service-content-banner">
				<div
					className="content-banner-bg"
					style={{
						background: `linear-gradient(135deg, ${servizio.accentColor}15 0%, transparent 50%)`,
					}}
				></div>
				<div className="container-modern">
					<div className="content-banner-layout">
						<div className="content-banner-icon-area" style={{ '--accent': servizio.accentColor }}>
							<iconify-icon icon={servizio.icon}></iconify-icon>
						</div>
						<div className="content-banner-text">
							<h2 className="content-banner-title">{t('services.ourApproach')}</h2>
							<p className="content-banner-description">
								{longDescription}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Features */}
			<section className="service-features">
				<div className="container-modern">
					<div className="title-box text-center">
						<h3 className="title-a">{t('services.whatWeOffer')}</h3>
						<div className="line-mf"></div>
					</div>
					<div className="grid grid-3">
						{features.map((feature, index) => (
							<div key={index} className="feature-card card-modern">
								<div
									className="feature-icon"
									style={{ '--accent': servizio.accentColor }}
								>
									<iconify-icon icon={servizio.features[index]?.icon || 'carbon:star'}></iconify-icon>
								</div>
								<h4 className="feature-title">{feature.title}</h4>
								<p className="feature-description">{feature.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* CTA intermedio */}
			<section
				className="service-info-cta"
				style={{ background: servizio.gradient }}
			>
				<div className="container-modern">
					<div className="info-cta-layout">
						<div className="info-cta-text">
							<h3>{t('services.wantToKnowMore')}</h3>
							<p>{t('services.teamAvailable')}</p>
						</div>
						<a href="/#contact" onClick={goToContact} className="btn-modern btn-hero-cta btn-lg">
							<iconify-icon icon="carbon:phone"></iconify-icon>
							{t('services.contactNow')}
						</a>
					</div>
				</div>
			</section>

			{/* Use Cases */}
			<section className="service-use-cases sect-pt4">
				<div className="container-modern">
					<div className="title-box text-center">
						<h3 className="title-a">{t('services.useCases')}</h3>
						<div className="line-mf"></div>
					</div>
					<div className="use-cases-grid">
						{useCases.map((useCase, index) => (
							<div key={index} className="use-case-item card-modern">
								<div
									className="use-case-number"
									style={{ color: servizio.accentColor }}
								>
									{String(index + 1).padStart(2, '0')}
								</div>
								<p className="use-case-text">{useCase}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Technologies */}
			<section className="service-technologies">
				<div className="container-modern">
					<div className="title-box text-center">
						<h3 className="title-a">{t('services.technologies')}</h3>
						<div className="line-mf"></div>
					</div>
					<div className="tech-tags">
						{servizio.technologies.map((tech, index) => (
							<span
								key={index}
								className="tech-tag"
								style={{
									borderColor: servizio.accentColor,
									color: servizio.accentColor,
								}}
							>
								{tech}
							</span>
						))}
					</div>
				</div>
			</section>

			{/* CTA finale */}
			<section className="service-cta">
				<div className="container-modern text-center">
					<div className="service-cta-icon">
						<iconify-icon icon="carbon:email"></iconify-icon>
					</div>
					<h3 className="service-cta-title">
						{t('services.interestedIn')} {title}?
					</h3>
					<p className="service-cta-text">
						{t('services.ctaText')}
					</p>
					<div className="service-cta-buttons">
						<a href="/#contact" onClick={goToContact} className="btn-modern btn-primary btn-lg">
							<iconify-icon icon="carbon:send--alt"></iconify-icon>
							{t('services.requestInfo')}
						</a>
						<Link to="/#about" className="btn-modern btn-secondary btn-lg">
							{t('services.viewAllServices')}
						</Link>
					</div>
				</div>
			</section>

		</React.Fragment>
	);
};

export default ServicePage;
